import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Modal,
  Switch,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export const GROWTH_PINK = '#ED1E79';
export const GROWTH_PINK_HOVER = '#d0176a';
export const GROWTH_FONT = "'Poppins', sans-serif";

/**
 * Neutrals for Growth modals.
 *
 * Surfaces are plain white — the pink header gradient, the tinted footer and
 * the tinted field fills have been removed. Structure is carried by hairlines
 * and type, and the brand pink is spent only on the primary action, the active
 * control and focus rings, so the eye lands on Save rather than on chrome.
 *
 * The greys carry a slight violet bias so they sit with the pink instead of
 * reading as a separate, colder palette.
 */
export const GROWTH_INK = '#14121A';
export const GROWTH_INK_2 = '#3A343F';
export const GROWTH_MUTED = '#6B6570';
export const GROWTH_FAINT = '#9A939F';
export const GROWTH_LINE = '#EAE6EC';
export const GROWTH_LINE_STRONG = '#D9D3DC';
export const GROWTH_ACCENT_SOFT = 'rgba(237, 30, 121, 0.10)';
export const GROWTH_FOCUS_RING = 'rgba(237, 30, 121, 0.28)';
/** Platform minimum for a comfortable click/tap target. */
export const GROWTH_CONTROL_MIN_H = 44;

/** Shared panel style for Growth feature modals (Poppins + Shino pink). */
export const growthModalPanelSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '94%', sm: 600, md: 640 },
  maxWidth: '96vw',
  maxHeight: '90vh',
  overflow: 'auto',
  borderRadius: '14px',
  bgcolor: '#FFFFFF',
  fontFamily: GROWTH_FONT,
  border: `1px solid ${GROWTH_LINE}`,
  // Neutral shadow. A pink-tinted one read as a coloured halo around the panel.
  boxShadow:
    '0 24px 60px -18px rgba(20, 18, 26, 0.30), 0 2px 6px rgba(20, 18, 26, 0.06)',
  outline: 'none',
  p: 0,
};

export const growthPrimaryBtnSx = {
  fontFamily: GROWTH_FONT,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '9px',
  px: 2.75,
  py: 1.25,
  minHeight: GROWTH_CONTROL_MIN_H,
  backgroundColor: GROWTH_PINK,
  color: '#fff',
  boxShadow: '0 6px 14px rgba(237, 30, 121, 0.22)',
  '&:hover': {
    backgroundColor: GROWTH_PINK_HOVER,
    boxShadow: '0 8px 16px rgba(237, 30, 121, 0.28)',
  },
  '&.Mui-disabled': {
    // Neutral, not a washed-out pink — a disabled Save should read as
    // unavailable rather than as a second brand colour.
    backgroundColor: GROWTH_LINE,
    color: GROWTH_FAINT,
    boxShadow: 'none',
  },
};

export const growthSecondaryBtnSx = {
  fontFamily: GROWTH_FONT,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '9px',
  px: 2.75,
  py: 1.25,
  minHeight: GROWTH_CONTROL_MIN_H,
  // Quiet, unfilled — the cancel action should not compete with Save.
  color: GROWTH_MUTED,
  borderColor: GROWTH_LINE_STRONG,
  backgroundColor: 'transparent',
  '&:hover': {
    borderColor: GROWTH_MUTED,
    color: GROWTH_INK,
    backgroundColor: 'transparent',
  },
};

/** Shared TextField look for Growth modals. */
export const growthTextFieldSx = {
  fontFamily: GROWTH_FONT,
  '& .MuiOutlinedInput-root': {
    borderRadius: '9px',
    fontFamily: GROWTH_FONT,
    backgroundColor: 'transparent',
    minHeight: GROWTH_CONTROL_MIN_H,
    '& fieldset': { borderColor: GROWTH_LINE_STRONG },
    '&:hover fieldset': { borderColor: GROWTH_MUTED },
    // A ring rather than a thicker border, so focus is unmissable and the
    // control does not shift by a pixel when it gains focus.
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${GROWTH_FOCUS_RING}` },
    '&.Mui-focused fieldset': { borderColor: GROWTH_PINK, borderWidth: '1px' },
  },
  '& .MuiInputLabel-root': { fontFamily: GROWTH_FONT, color: GROWTH_MUTED },
  '& .MuiInputLabel-root.Mui-focused': { color: GROWTH_PINK },
};

/** Modal body — consistent field spacing and typography. */
export const growthFormBodySx = {
  px: 3,
  pt: 0.5,
  pb: 2.5,
  fontFamily: GROWTH_FONT,
  '& .MuiOutlinedInput-root': {
    borderRadius: '9px',
    fontFamily: GROWTH_FONT,
    backgroundColor: 'transparent',
    minHeight: GROWTH_CONTROL_MIN_H,
  },
  '& .MuiFormControl-root': { fontFamily: GROWTH_FONT, width: '100%' },
  '& .MuiTextField-root': { width: '100%' },
  '& .MuiFormHelperText-root': {
    fontFamily: GROWTH_FONT,
    mx: 0,
    mt: 0.75,
    fontSize: 11.5,
    color: GROWTH_MUTED,
    lineHeight: 1.45,
  },
  '& .MuiInputLabel-root': { fontFamily: GROWTH_FONT, color: GROWTH_MUTED },
  '& fieldset': { borderColor: GROWTH_LINE_STRONG },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: GROWTH_MUTED },
  '& .MuiOutlinedInput-root.Mui-focused': {
    boxShadow: `0 0 0 3px ${GROWTH_FOCUS_RING}`,
  },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderColor: GROWTH_PINK,
    borderWidth: '1px',
  },
  '& .MuiInputLabel-root.Mui-focused': { color: GROWTH_PINK },
};

/** Two-column form grid with aligned row heights. */
export function GrowthFormGrid({ children, spacing = 2 }) {
  return (
    <Grid
      container
      spacing={spacing}
      sx={{
        width: '100%',
        '& > .MuiGrid-item': {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        },
      }}
    >
      {children}
    </Grid>
  );
}

/** Section divider inside Growth modals. */
export function GrowthFormSection({ title }) {
  return (
    <Grid item xs={12} sx={{ pt: 0.5 }}>
      {/* Label + trailing rule: names the group without boxing it in. */}
      <Box
        sx={{
          pt: 2,
          pb: 0.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
        }}
      >
        <Typography
          sx={{
            fontFamily: GROWTH_FONT,
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            color: GROWTH_FAINT,
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: GROWTH_LINE }} />
      </Box>
    </Grid>
  );
}

/** Toggle row — same visual height as text fields for clean 2-col rows. */
export function GrowthFormSwitch({ label, checked, onChange, helperText }) {
  return (
    <Box
      sx={{
        width: '100%',
        minHeight: 56,
        px: 1.5,
        py: 0.75,
        borderRadius: '9px',
        border: `1px solid ${GROWTH_LINE}`,
        bgcolor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        flex: 1,
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={onChange}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: GROWTH_PINK },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: GROWTH_PINK,
              },
            }}
          />
        }
        label={
          <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 13.5, color: '#333333' }}>
            {label}
          </Typography>
        }
        sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
      />
      {helperText ? (
        <Typography
          sx={{
            fontFamily: GROWTH_FONT,
            fontSize: 11.5,
            color: GROWTH_MUTED,
            pl: 5.5,
            mt: -0.25,
            lineHeight: 1.35,
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

/** PaperProps for MUI Dialogs on Growth pages (subscription plans, etc.). */
export const growthDialogPaperSx = {
  fontFamily: GROWTH_FONT,
  borderRadius: '14px',
  border: `1px solid ${GROWTH_LINE}`,
  boxShadow:
    '0 24px 60px -18px rgba(20, 18, 26, 0.30), 0 2px 6px rgba(20, 18, 26, 0.06)',
  overflow: 'hidden',
};

export const growthDialogTitleSx = {
  fontFamily: GROWTH_FONT,
  fontWeight: 600,
  fontSize: 19,
  letterSpacing: '-0.015em',
  color: GROWTH_INK,
  background: 'transparent',
  borderBottom: `1px solid ${GROWTH_LINE}`,
  py: 2,
  px: 3,
};

export const growthDialogActionsSx = {
  px: 3,
  py: 2,
  gap: 1.25,
  borderTop: `1px solid ${GROWTH_LINE}`,
  backgroundColor: 'transparent',
};

export function GrowthPrimaryButton({ loading, children, disabled, startIcon, ...props }) {
  return (
    <Button
      variant="contained"
      disableElevation
      disabled={disabled || loading}
      startIcon={
        loading ? <CircularProgress size={16} color="inherit" /> : startIcon
      }
      sx={growthPrimaryBtnSx}
      {...props}
    >
      {children}
    </Button>
  );
}

export function GrowthSecondaryButton({ loading, children, disabled, ...props }) {
  return (
    <Button
      variant="outlined"
      disabled={disabled || loading}
      sx={growthSecondaryBtnSx}
      {...props}
    >
      {children}
    </Button>
  );
}

/** Full-page loading state for Growth feature screens. */
export function GrowthPageLoader({ label = 'Loading…' }) {
  return (
    <Box
      sx={{
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        fontFamily: GROWTH_FONT,
      }}
    >
      <CircularProgress size={36} sx={{ color: GROWTH_PINK }} />
      <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 13.5, color: '#6B7280' }}>
        {label}
      </Typography>
    </Box>
  );
}

/** Dim overlay while create / update / delete is in flight. */
export function GrowthBusyOverlay({ open, label = 'Saving…' }) {
  if (!open) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        bgcolor: 'rgba(255,255,255,0.72)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        borderRadius: 'inherit',
      }}
    >
      <CircularProgress size={32} sx={{ color: GROWTH_PINK }} />
      <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 13, color: '#6B7280' }}>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Enhanced modal shell for Growth Features admin pages.
 */
export function GrowthModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  maxWidth,
  busy = false,
  busyLabel = 'Saving…',
}) {
  return (
    <Modal
      open={open}
      onClose={busy ? undefined : onClose}
      sx={{ zIndex: 1300 }}
    >
      <Box
        sx={{
          ...growthModalPanelSx,
          position: 'relative',
          ...(maxWidth ? { width: maxWidth } : null),
        }}
      >
        <GrowthBusyOverlay open={busy} label={busyLabel} />
        <Box
          sx={{
            px: 3,
            pt: 2.75,
            pb: 2,
            background: 'transparent',
            borderBottom: `1px solid ${GROWTH_LINE}`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: GROWTH_FONT,
                fontSize: 19,
                fontWeight: 600,
                letterSpacing: '-0.015em',
                color: GROWTH_INK,
                lineHeight: 1.25,
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography
                sx={{
                  mt: 0.625,
                  fontFamily: GROWTH_FONT,
                  fontSize: 13,
                  color: GROWTH_MUTED,
                  lineHeight: 1.5,
                }}
              >
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            disabled={busy}
            sx={{
              color: GROWTH_FAINT,
              backgroundColor: 'transparent',
              border: 0,
              borderRadius: '8px',
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: GROWTH_ACCENT_SOFT,
                color: GROWTH_PINK,
              },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={growthFormBodySx}>
          {children}
        </Box>

        {actions ? (
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.25,
              borderTop: `1px solid ${GROWTH_LINE}`,
              backgroundColor: 'transparent',
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Box>
    </Modal>
  );
}

export default GrowthModal;
